<template>
    <div
        class="p-6 bg-slate-300/30 rounded-2xl shadow-inner border-solid border-b border-t-0 border-x-0 border-white flex-col gap-4 flex"
    >
        <kiosk-ten-dlc-checkbox-filter
            label="Vendor"
            name="vendor"
            :options="vendorOptions"
            v-model="vendor"
        ></kiosk-ten-dlc-checkbox-filter>
        <div data-test="brands-selectable">
            <div class="font-bold text-xs text-slate-700 mb-2">Brand</div>
            <vx-selectable
                :modelValue="filters.brand_id"
                placeholder="-"
                :reduce="(option: { label: string; value: string }) => option.value"
                :loading="loadingBrands"
                :options="brandOptions"
                @search="searchBrands"
                @update:modelValue="onBrandChange"
                :clearable="false"
            >
            </vx-selectable>
        </div>
        <div data-test="campaigns-selectable">
            <div class="font-bold text-xs text-slate-700 mb-2">Campaign</div>
            <vx-selectable
                v-model="filters.campaign_id"
                placeholder="-"
                :reduce="(option: { label: string; value: string }) => option.value"
                :loading="loadingCampaigns"
                :options="campaignOptions"
                @search="searchCampaigns"
                :clearable="false"
            >
            </vx-selectable>
        </div>
        <div data-test="teams-selectable">
            <div class="font-bold text-xs text-slate-700 mb-2">Team</div>
            <vx-selectable
                v-model="filters.team_id"
                placeholder="-"
                :reduce="(option: { label: string; value: number }) => option.value"
                :loading="loadingTeams"
                :options="teamOptions"
                @search="searchTeams"
                :clearable="false"
            >
            </vx-selectable>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { VxSelectable } from '@voxie/frontend-components';
import { ref, watch } from 'vue';
import { useTcrBrandOptions } from '~/components/kiosk/kiosk-ten-dlc/composables/useTcrBrandOptions';
import { scalarSelection } from '~/components/kiosk/kiosk-ten-dlc/filterSelection';
import KioskTenDlcCheckboxFilter from '~/components/kiosk/kiosk-ten-dlc/list/KioskTenDlcCheckboxFilter.vue';
import { vendorLabels } from '~/components/kiosk/kiosk-ten-dlc/tenDlcLabels';
import { useToasted } from '~/composables/useToasted';
import { adminTeamsClient, adminTenDlcClient } from '~/services';
import type { TcrListParams } from '~/services/adminTenDlcClient.types';
import debounce from '~/utils/debounce';

const toasted = useToasted();

const vendorOptions: { label: string; value: NonNullable<TcrListParams['vendor']> }[] = [
    { label: vendorLabels.bandwidth, value: 'bandwidth' },
    { label: vendorLabels.twilio, value: 'twilio' },
];

const filters = defineModel<TcrListParams>({ default: () => ({}) });

const vendor = scalarSelection(
    () => filters.value.vendor,
    (value) => {
        filters.value.vendor = value;
    }
);

const { brandOptions, loadingBrands, searchBrands } = useTcrBrandOptions(() => filters.value.brand_id);

const campaignOptions = ref<{ label: string; value: string }[]>([]);
const loadingCampaigns = ref(false);

const searchCampaigns = debounce((query?: string) => {
    loadingCampaigns.value = true;

    adminTenDlcClient
        .getTcrCampaigns({ query, brand_id: filters.value.brand_id })
        .then((response) => {
            campaignOptions.value = response.data.data.map((campaign) => ({
                value: campaign.campaign_id,
                label: campaign.campaign_id,
            }));
        })
        .catch((e) => {
            console.error(e);
            toasted.global.platform_error();
        })
        .finally(() => {
            loadingCampaigns.value = false;
        });
}, 300);

const teamOptions = ref<{ label: string; value: number }[]>([]);
const loadingTeams = ref(false);

const searchTeams = debounce((query?: string) => {
    loadingTeams.value = true;

    adminTeamsClient
        .getTeams({ query })
        .then((response) => {
            teamOptions.value = response.data.data.map((team) => ({
                value: team.id,
                label: `${team.id} - ${team.name}`.trim(),
            }));
        })
        .catch((e) => {
            console.error(e);
            toasted.global.platform_error();
        })
        .finally(() => {
            loadingTeams.value = false;
        });
}, 300);

const onBrandChange = (brandId?: string) => {
    // Set brand and clear the dependent campaign synchronously in one handler
    // so the page's multi-source watcher flushes once with both changes applied.
    filters.value.brand_id = brandId;
    filters.value.campaign_id = undefined;
};

// Watching instead of rescoping inside onBrandChange keeps the campaign
// options correct when the brand changes from outside too, e.g. a page reset.
watch(
    () => filters.value.brand_id,
    () => {
        searchCampaigns();
    }
);

searchBrands();
searchCampaigns();
searchTeams();
</script>
